"use client"

import styled from "@emotion/styled"
import { PackageOption } from "@eurodrive/utils"
import { Typography } from "@mui/material"
import Image from "next/image"
import { Dispatch, SetStateAction } from "react"
import { Config } from "./CarConfiguration"

const StyledLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 22px;
`

const ContentLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const PackageCard = styled.div<{ selected: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
  border: 1px solid ${({ selected }) => (selected ? "#000000" : "#ECE9E9")};
  border-radius: 8px;
  background-color: #ffffff;
  cursor: pointer;
  transition: border-color 0.2s ease;

  &:hover {
    border-color: #000000;
  }
`

const PackageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`

const PackageNameContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const PackageDescription = styled(Typography)`
  width: 100%;
`

function formatPrice(price: number): string {
  return new Intl.NumberFormat("he-IL", {
    style: "currency",
    maximumFractionDigits: 0,
    currency: "ILS",
  }).format(price)
}

function getPackageIcon(packageName: string): string {
  if (packageName === "בסיס") return "/icons/shield-tick.svg"
  if (packageName === "פרמיום פלוס") return "/icons/security-safe.svg"
  if (packageName === "פלטינום") return "/icons/flash-circle.svg"
  return "/icons/shield-tick.svg"
}

export default function PackagesStage({
  markComplete,
  additionalPackages,
  config,
  setConfig,
}: {
  markComplete: () => void
  additionalPackages: PackageOption[]
  config: Config
  setConfig: Dispatch<SetStateAction<Config>>
}) {
  const handleSelectPackage = (index: number) => {
    setConfig((prev) => ({
      ...prev,
      packages: index,
    }))
    markComplete()
  }

  const selectedPackage = config.packages ?? null

  return (
    <StyledLayout>
      <Typography variant="h2" component="h2">
        בחירת חבילות
      </Typography>
      <ContentLayout>
        {additionalPackages.length > 0 ? (
          additionalPackages.map((pkg, index) => {
            const isSelected = selectedPackage === index
            const iconSrc = getPackageIcon(pkg.name)

            return (
              <PackageCard
                key={index}
                selected={isSelected}
                onClick={() => handleSelectPackage(index)}
              >
                <PackageHeader>
                  <PackageNameContainer>
                    <Image
                      src={iconSrc}
                      alt=""
                      width={24}
                      height={24}
                      aria-hidden="true"
                    />
                    <Typography variant="body2" sx={{ fontSize: "16px" }}>
                      {pkg.name}
                    </Typography>
                  </PackageNameContainer>
                  <Typography variant="body1" sx={{ fontSize: "18px" }}>
                    {pkg.monthlyPrice > 0
                      ? `${formatPrice(pkg.monthlyPrice)} חודשי`
                      : "כלול במחיר"}
                  </Typography>
                </PackageHeader>
                {pkg.description && (
                  <PackageDescription variant="body3" sx={{ fontSize: "14px" }}>
                    {pkg.description}
                  </PackageDescription>
                )}
              </PackageCard>
            )
          })
        ) : (
          <Typography
            variant="body3"
            color="textSecondary"
            style={{ textAlign: "center", padding: "20px" }}
          >
            אין חבילות נוספות זמינות
          </Typography>
        )}
      </ContentLayout>
    </StyledLayout>
  )
}
